import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Loader } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import './CheckoutPages.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const { user, token } = useAuth();
  const navigate = useNavigate();

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(
        `${API_URL}/api/checkout/status/${sessionId}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );

      const data = response.data;

      if (data.payment_status === 'paid') {
        setStatus('success');
        setPaymentData(data);
        // Store session ID for linking subscription after registration
        sessionStorage.setItem('bt_checkout_session', sessionId);
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setStatus('processing');
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } else {
        setStatus('error');
      }
    }
  };

  return (
    <div className="checkout-page" data-testid="checkout-success-page">
      <div className="checkout-container">
        {status === 'loading' || status === 'processing' ? (
          <div className="checkout-status" data-testid="loading-status">
            <Loader size={48} className="spinner" />
            <h2>Processing Your Order</h2>
            <p>Please wait while we confirm your payment...</p>
          </div>
        ) : status === 'success' ? (
          (() => {
            const productId = paymentData?.metadata?.product_id || '';
            const isLegacy = productId.startsWith('legacy-');
            const collectionName =
              productId === 'legacy-thread' ? 'A Thread'
              : productId === 'legacy-woven' ? 'Woven'
              : productId === 'legacy-tapestry' ? 'Tapestry'
              : 'your collection';
            const letters =
              productId === 'legacy-thread' ? 3
              : productId === 'legacy-woven' ? 6
              : productId === 'legacy-tapestry' ? 12
              : 0;

            if (isLegacy) {
              const onboardingToken = paymentData?.onboarding_token;
              const onboardingHref = onboardingToken ? `/legacy/onboarding/${onboardingToken}` : null;
              return (
                <div className="checkout-status success legacy-success" data-testid="legacy-success-status">
                  <div className="success-icon">
                    <CheckCircle size={64} />
                  </div>
                  <h1>Thank you for choosing {collectionName}.</h1>
                  <p className="success-message" style={{ fontStyle: 'italic', maxWidth: 580, margin: '0 auto 24px' }}>
                    You&apos;ve just begun preserving something irreplaceable. We are honoured to be part of it.
                  </p>
                  <p style={{ maxWidth: 560, margin: '0 auto 32px', lineHeight: 1.75 }}>
                    A confirmation and welcome email has just been sent to your inbox with everything you need &mdash;
                    the free call number, how to share it with your loved one, and what happens next.
                    Your keepsake tin will arrive in the post with your first letter.
                  </p>

                  {onboardingHref && (
                    <div className="legacy-onb-cta" data-testid="legacy-onboarding-cta-box" style={{
                      background: '#8b9a7d',
                      borderRadius: 12, padding: '24px 28px', margin: '0 auto 36px',
                      maxWidth: 560, color: '#ffffff', textAlign: 'center'
                    }}>
                      <p style={{ margin: '0 0 6px', fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#ffffff', fontWeight: 600 }}>One quick next step</p>
                      <h3 style={{ margin: '0 0 10px', fontFamily: 'Cormorant Garamond, Georgia, serif', fontStyle: 'italic', fontWeight: 400, fontSize: 24, color: '#ffffff' }}>Tell us about your loved one</h3>
                      <p style={{ margin: '0 0 16px', fontSize: 15, lineHeight: 1.6, color: '#ffffff' }}>
                        So we know who is calling, where to post your keepsake tin, and how to address every letter. It takes about two minutes.
                      </p>
                      <Link
                        to={onboardingHref}
                        className="btn"
                        data-testid="legacy-onboarding-cta-btn"
                        style={{ background: '#ffffff', color: '#4a5b42', padding: '12px 28px', borderRadius: 6, textDecoration: 'none', fontWeight: 600, display: 'inline-block' }}
                      >
                        Complete onboarding &rarr;
                      </Link>
                      <p style={{ margin: '14px 0 0', fontSize: 12, color: '#f0ede4', fontStyle: 'italic' }}>
                        You can also use the link in your welcome email to return any time.
                      </p>
                    </div>
                  )}

                  <div className="next-steps" style={{ textAlign: 'left', maxWidth: 560, margin: '0 auto 36px' }}>
                    <h3 style={{ textAlign: 'center' }}>Your {collectionName} collection includes</h3>
                    <ul style={{ listStyle: 'none', padding: 0, marginTop: 14 }}>
                      <li style={{ padding: '8px 0', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                        <strong>{letters}</strong> beautifully crafted keepsake letter{letters !== 1 ? 's' : ''}
                      </li>
                      <li style={{ padding: '8px 0', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                        The Binding Thread keepsake tin
                      </li>
                      <li style={{ padding: '8px 0', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                        Shipping included
                      </li>
                      <li style={{ padding: '8px 0' }}>
                        A free toll-free number your loved one can call any time, day or night
                      </li>
                    </ul>
                  </div>

                  <div className="checkout-actions">
                    <Link to="/" className="btn btn-secondary" data-testid="home-btn">
                      Return to Binding Thread
                    </Link>
                  </div>

                  <p style={{ marginTop: 36, fontSize: 13, color: '#888', fontStyle: 'italic' }}>
                    If you need to talk to us at any point, simply reply to your confirmation email or
                    write to admin@bindingthread.com.au — we&apos;re real people and we&apos;re here for you.
                  </p>
                </div>
              );
            }

            return (
              <div className="checkout-status success" data-testid="success-status">
                <div className="success-icon">
                  <CheckCircle size={64} />
                </div>
                <h1>Welcome to the Family</h1>
                <p className="success-message">
                  You and your loved one are now part of the Binding Thread family.
                </p>

                <div className="next-steps">
                  <h3>What Happens Next?</h3>
                  <ol>
                    <li>Complete your onboarding to tell us about your loved one</li>
                    <li>Once you have completed our onboarding process, we will send an SMS to your loved one letting them know they will receive a welcome pack from us this week with all the information they will need to use our service</li>
                    <li>Your first letter will arrive in approx. 4 weeks</li>
                  </ol>
                </div>

                <div className="checkout-actions">
                  {user ? (
                    <Link to="/onboarding" className="btn btn-primary" data-testid="onboarding-btn">
                      Complete Onboarding
                    </Link>
                  ) : (
                    <Link to="/register" className="btn btn-primary" data-testid="register-btn">
                      Create Account to Continue
                    </Link>
                  )}
                  <Link to="/" className="btn btn-secondary" data-testid="home-btn">
                    Return Home
                  </Link>
                </div>
              </div>
            );
          })()
        ) : (
          <div className="checkout-status error" data-testid="error-status">
            <h2>Something Went Wrong</h2>
            <p>
              {status === 'timeout' 
                ? 'Payment verification timed out. Please check your email for confirmation.'
                : status === 'expired'
                ? 'This payment session has expired.'
                : 'We couldn\'t verify your payment. Please contact support.'}
            </p>
            <Link to="/shop" className="btn btn-secondary">
              Return to Shop
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

export default CheckoutSuccessPage;
